/**
 * Per-person sidebar prefs. Painted instantly from the snapshot (the Worker inlines prefs with
 * the page), saved optimistically, synced to the server, and mirrored to localStorage so a
 * reload paints the same sidebar even before the snapshot arrives.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import { emptyPrefs, moveTab, type CustomView, type NavPrefs, type TabId } from "./navConfig";
import { toastStore } from "../stores/toastStore";

interface NavState {
  prefs: NavPrefs;
  editing: boolean;
  /** The rail folded to its icons (wide screens; this browser only, not synced). Edit sidebar always unfolds it. */
  collapsed: boolean;
  hydrate: (p: NavPrefs) => void;
  setEditing: (v: boolean) => void;
  setCollapsed: (v: boolean) => void;
  move: (tab: TabId, toIndex: number, currentOrder: TabId[]) => void;
  toggleHidden: (tab: TabId) => void;
  addView: (v: CustomView) => void;
  removeView: (id: string) => void;
  hideTeamView: (id: string) => void;
  /** Star / unstar a dashboard (Analytics). Starred ones show under Analytics in the sidebar. */
  toggleStar: (id: string, title: string) => void;
  moveStar: (id: string, toIndex: number) => void;
  reset: () => void;
}

/** Prefs saved before a field existed get its default (e.g. `starred`). */
const withDefaults = (p: Partial<NavPrefs>): NavPrefs => ({ ...emptyPrefs(), ...p, starred: p.starred ?? [] });

const LS_KEY = "dashboard:navPrefs";
const LS_COLLAPSED = "dashboard:railCollapsed";
const save = async (prefs: NavPrefs) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(prefs)); } catch { /* private mode */ }
  await fetch("/api/me/nav", { method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(prefs) });
};

export const navStore = createStore<NavState>()((set, get) => {
  /** Apply a change, persist it, and offer Undo. */
  const commit = (next: NavPrefs, message: string) => {
    const prev = get().prefs;
    set({ prefs: next });
    void save(next).catch(() => {
      set({ prefs: prev });
      toastStore.getState().show({ message: "Couldn't save your sidebar. Try again." });
    });
    toastStore.getState().show({
      message,
      actionLabel: "Undo",
      onAction: () => { set({ prefs: prev }); void save(prev); },
    });
  };

  return {
    prefs: (() => { try { return withDefaults(JSON.parse(localStorage.getItem(LS_KEY) ?? "")); } catch { return emptyPrefs(); } })(),
    editing: false,
    collapsed: (() => { try { return localStorage.getItem(LS_COLLAPSED) === "1"; } catch { return false; } })(),
    hydrate: (p) => set({ prefs: withDefaults(p) }),
    setEditing: (editing) => set({ editing }),
    setCollapsed: (collapsed) => { set({ collapsed }); try { localStorage.setItem(LS_COLLAPSED, collapsed ? "1" : "0"); } catch { /* private mode */ } },
    move: (tab, toIndex, currentOrder) =>
      commit({ ...get().prefs, order: moveTab(currentOrder, tab, toIndex), knownTabs: currentOrder }, "Sidebar updated"),
    toggleHidden: (tab) => {
      const p = get().prefs;
      const hidden = p.hidden.includes(tab) ? p.hidden.filter((t) => t !== tab) : [...p.hidden, tab];
      commit({ ...p, hidden, knownTabs: Array.from(new Set([...p.knownTabs, tab])) }, hidden.includes(tab) ? "Tab hidden" : "Tab shown");
    },
    addView: (v) => commit({ ...get().prefs, views: [...get().prefs.views, v] }, `View “${v.label}” added`),
    removeView: (id) => commit({ ...get().prefs, views: get().prefs.views.filter((v) => v.id !== id) }, "View removed"),
    hideTeamView: (id) => commit({ ...get().prefs, hiddenTeamViews: [...get().prefs.hiddenTeamViews, id] }, "Team view hidden for you"),
    toggleStar: (id, title) => {
      const p = get().prefs;
      const on = p.starred.includes(id);
      commit({ ...p, starred: on ? p.starred.filter((x) => x !== id) : [...p.starred, id] }, on ? `“${title}” removed from your sidebar` : `“${title}” starred · now in your sidebar`);
    },
    moveStar: (id, toIndex) => {
      const rest = get().prefs.starred.filter((x) => x !== id);
      const at = Math.max(0, Math.min(toIndex, rest.length));
      commit({ ...get().prefs, starred: [...rest.slice(0, at), id, ...rest.slice(at)] }, "Starred dashboards reordered");
    },
    // Stars and personal views are the user's own choices, so a sidebar reset keeps them.
    reset: () => commit({ ...emptyPrefs(), views: get().prefs.views, starred: get().prefs.starred }, "Sidebar reset to your group's default"),
  };
});

export const useNav = <T,>(selector: (s: NavState) => T) => useStore(navStore, selector);
